import AddIcon from '@mui/icons-material/Add';
import NoteIcon from '@mui/icons-material/NoteAlt';
import Timeline from '@mui/lab/Timeline';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Paper,
  Stack,
} from '@mui/material';
import NoteTypeSelect from '../../../../../components/EnumSelects/NoteTypeSelect';
import NoteItem from '../../../../../components/NoteItem';
import {
  getHorseNoteHighlights,
  getHorseNotes,
} from '../../../../../redux/horse/actions';
import {
  DEFAULT_NOTE_TYPE_FILTERS,
  initializeNoteTypeFilter,
  updateNoteTypeFilter,
} from '../../../../../redux/noteTypeFilters/actions';
import { selectNoteTypeFilters } from '../../../../../redux/noteTypeFilters/selectors';

const NotesCardComponent = ({
  id,
  isAdmin,
  openAddEditNoteModal,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const horseNotes = useSelector((state) => state.horse.horseNotes);
  const isFetchingHorseNotes = useSelector((state) => state.horse.isFetchingHorseNotes);
  const barnUserId = useSelector((state) => (
    state.user?.barnUser?.id
    || state.user?.user?.id
    || state.user?.me?.id
    || null
  ));
  const noteTypeFilter = useSelector((state) => selectNoteTypeFilters(state));

  // State
  const [pageSize] = React.useState(25);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'createdTimestamp',
      sort: 'desc',
    },
  ]);
  const resolvedNoteTypeFilter = noteTypeFilter && noteTypeFilter.length
    ? noteTypeFilter
    : DEFAULT_NOTE_TYPE_FILTERS;

  React.useEffect(() => {
    dispatch(initializeNoteTypeFilter({
      userId: barnUserId,
      fallback: DEFAULT_NOTE_TYPE_FILTERS,
    }));
  }, [dispatch, barnUserId]);

  React.useEffect(() => {
    dispatch(getHorseNotes(
      id,
      (page - 1),
      pageSize,
      sortModel.map((s) => `&sort=${s.field},${s.sort}`),
      null,
      resolvedNoteTypeFilter || null,
    ));
    dispatch(getHorseNoteHighlights(id));
  }, [dispatch, id, page, pageSize, sortModel, resolvedNoteTypeFilter]);

  const handleNoteTypeFilterChange = React.useCallback((event) => {
    dispatch(updateNoteTypeFilter({
      userId: barnUserId,
      value: event.target.value,
    }));
  }, [dispatch, barnUserId]);

  const closeAddEditModalCallbcak = () => {
    // reload
    dispatch(getHorseNotes(
      id,
      (page - 1),
      pageSize,
      sortModel.map((s) => `&sort=${s.field},${s.sort}`),
      null,
      resolvedNoteTypeFilter || null,
    ));
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        p: 2,
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={2}
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar><NoteIcon /></Avatar>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Horse Notes
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flex: 1,
          }}
        >
          <NoteTypeSelect value={noteTypeFilter} onChange={handleNoteTypeFilterChange} includeAllOption multiple size="small" />
          <Button
            sx={{
              width: 120,
              minWidth: '120px',
            }}
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openAddEditNoteModal(null, null, closeAddEditModalCallbcak)}
          >
            Add Note
          </Button>
        </Box>
      </Stack>
      <Timeline>
        {
          horseNotes.content.map((note) => (
            <NoteItem
              note={note}
              horseId={id}
              pinnedHorseNoteId={horse.activeNote
                ? horse.activeNote.id
                : null}
              key={note.id}
              includeUserInfo
              isLoading={isFetchingHorseNotes}
              onEdit={() => openAddEditNoteModal(note, noteTypeFilter, closeAddEditModalCallbcak)}
              isAdmin={isAdmin}
            />
          ))
        }

      </Timeline>

      {(horseNotes.totalPages || 0) > 1 && (
        <Pagination count={horseNotes.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseNotes} />
      )}

    </Paper>
  );
};

NotesCardComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
};
NotesCardComponent.defaultProps = {};

export default NotesCardComponent;
